<template>
  <Collapse
    v-if="isHKlandUser || isTenant"
    class="green-operation__assessment green-operation__air-conditioning"
  >
    <template #title>
      <HKLLeftRight>
        <span class="font-bold">{{ $t('tenancy.greenOperation.assessments.airConditioning.title') }}</span>
        <HKLInputPoints
          v-if="!isEdit && canViewPoint"
          v-model="points"
          :isEdit="false"
        />
      </HKLLeftRight>
    </template>
    <template #content>
      <div
        v-if="buildingComfyInstallation && isHKlandUser"
      >
        <HKLLeftRight class="green-operation__assessment-title">
          <div class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.airConditioning.installationOfComfy') }}
          </div>
        </HKLLeftRight>
        <div
          class="green-operation__assessment-form row"
        >
          <template>
            <HKLRadioButton
              v-model="installationOfComfy.value"
              :disabled="!isEdit"
              class="small-6"
              value="since"
            >
              <template v-if="!isEdit">
                {{ $t('tenancy.greenOperation.assessments.airConditioning.installationOfComfyOptionSince', [since]) }}
              </template>
              <template v-else>
                <HKLText
                  class="air-conditioning__form-since"
                  :text="$t('tenancy.greenOperation.assessments.airConditioning.installationOfComfyOptionSince', [])"
                />
                <DatePicker
                  v-model="sinceDate"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                />
              </template>
            </HKLRadioButton>
            <HKLRadioButton
              v-model="installationOfComfy.value"
              :disabled="!isEdit"
              class="small-3"
              value="no"
            >
              {{ $t('action.no') }}
            </HKLRadioButton>
          </template>
        </div>
      </div>
      <div v-if="isHKlandUser">
        <HKLLeftRight class="green-operation__assessment-title">
          <div class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.airConditioning.hoursOfAcOperation') }}
          </div>
          <HKLInputPoints
            v-if="canViewPoint"
            v-model="hoursOfAcOperation.point"
            :isEdit="isEdit"
          />
        </HKLLeftRight>
        <div class="green-operation__assessment-form">
          <HKLRadioButton
            v-model="hoursOfAcOperation.value"
            :disabled="!isEdit"
            value="lte4"
          >
            {{ $t('tenancy.greenOperation.assessments.airConditioning.hoursOfAcOperationOption', ['≤4']) }}
          </HKLRadioButton>
          <HKLRadioButton
            v-model="hoursOfAcOperation.value"
            :disabled="!isEdit"
            value="lte6"
          >
            {{ $t('tenancy.greenOperation.assessments.airConditioning.hoursOfAcOperationOption', ['≤6']) }}
          </HKLRadioButton>
          <HKLRadioButton
            v-model="hoursOfAcOperation.value"
            :disabled="!isEdit"
            value="gt6"
          >
            {{ $t('tenancy.greenOperation.assessments.airConditioning.hoursOfAcOperationOption', ['＞6']) }}
          </HKLRadioButton>
        </div>
      </div>
      <div v-if="isHKlandUser">
        <HKLLeftRight class="green-operation__assessment-title">
          <div class="font-bold">
            {{ $t('tenancy.greenOperation.assessments.airConditioning.averageIndoorTemperatureSetpoint') }}
          </div>
          <HKLInputPoints
            v-if="canViewPoint"
            v-model="averageIndoorTemperatureSetpoint.point"
            :isEdit="isEdit"
          />
        </HKLLeftRight>
        <div class="green-operation__assessment-form row">
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="gte23.5"
          >
            ≥23.5℃
          </HKLRadioButton>
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="23.0-23.4"
          >
            23.0-23.4℃
          </HKLRadioButton>
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="22.5-22.9"
          >
            22.5-22.9℃
          </HKLRadioButton>
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="22.0-22.4"
          >
            22.0-22.4℃
          </HKLRadioButton>
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="lt22.0"
          >
            ＜22.0℃
          </HKLRadioButton>
          <HKLRadioButton
            v-model="averageIndoorTemperatureSetpoint.value"
            :disabled="!isEdit"
            class="small-4"
            value="na"
          >
            {{ $t('na') }}
          </HKLRadioButton>
        </div>
      </div>
      <template v-if="isTenant">
        <div
          v-for="({ title, value, enabled }, index) in airConditionForTenant"
          v-if="enabled"
          :key="index"
        >
          <div class="green-operation__assessment-title">
            <div class="font-bold">
              {{ title }}
            </div>
          </div>
          <div class="green-operation__assessment-form row">
            {{ value }}
          </div>
        </div>
      </template>
    </template>
  </Collapse>
</template>

<script>
import { computed, reactive, toRefs, watch, unref } from 'vue-demi'
import lodash from 'lodash'
import HKLRadioButton from '@/controls/Button/HKLRadioButton.vue'
import Collapse from '@/controls/Collapse.vue'
import DatePicker from '@/controls/DatePicker.vue'
import HKLText from '@/controls/HKLText.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import { useStoreGetters } from '@/store/index'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { useGreenOperationPerms } from '../GreenOperationLetter.hook'
import HKLInputPoints from '../components/HKLInputPoints.vue'
import { GREEN_OPERATION_ASSESSMENT_TYPE } from '../consts'
import { useGreenOperationAssessmentFormStatus } from '../hook'
import { useAssessmentForm } from './hook'
import { totalPoints } from './utils'

const ASSESSMENT_TYPE = GREEN_OPERATION_ASSESSMENT_TYPE.AIR_CONDITIONING

export default {
  components: {
    Collapse,
    HKLLeftRight,
    HKLRadioButton,
    HKLInputPoints,
    HKLText,
    DatePicker,
  },
  setup () {
    const { isTenant, isHKlandUser } = useStoreGetters('user', ['isTenant', 'isHKlandUser'])
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const { canViewPoint } = useGreenOperationPerms()
    const { isEdit } = useGreenOperationAssessmentFormStatus()
    const formMap = reactive({
      installationOfComfy: useAssessmentForm(
        ASSESSMENT_TYPE,
        'installationOfComfy',
      ),
      installationOfComfySince: useAssessmentForm(
        ASSESSMENT_TYPE,
        'installationOfComfySince',
      ),
      hoursOfAcOperation: useAssessmentForm(
        ASSESSMENT_TYPE,
        'hoursOfAcOperation',
      ),
      averageIndoorTemperatureSetpoint: useAssessmentForm(
        ASSESSMENT_TYPE,
        'averageIndoorTemperatureSetpoint',
      ),
    })
    const sinceDate = computed({
      get () {
        const dateValue = formMap.installationOfComfySince.value
        if (typeof dateValue === 'string') return new Date(dateValue)
        return dateValue
      },
      set (val) {
        formMap.installationOfComfySince.value = val
      },
    })
    watch(() => formMap.installationOfComfy.value, (val) => {
      if (val !== 'since') sinceDate.value = null
    })

    const buildingComfyInstallation = computed(() => {
      return lodash.get(unref(currentTenancy), 'building.comfyInstallation', false)
    })
    const since = computed(() => {
      if (formMap.installationOfComfy.value !== 'since') return i18n.t('emptyValue')
      return dateUtil.formatByLongDate(formMap.installationOfComfySince.value) || i18n.t('emptyValue')
    })
    const titlePrefix = 'tenancy.greenOperation.assessments.airConditioning'
    const comfortForTenant = computed(() => {
      if (!formMap.installationOfComfy.value) return i18n.t('emptyValue')
      if (formMap.installationOfComfy.value === 'no') return i18n.t('action.no')
      return i18n.t('tenancy.greenOperation.assessments.airConditioning.installationOfComfyOptionSince', [unref(since)])
    })
    const hoursOfAcOpForTenant = computed(() => {
      const keyValMap = {
        lte4: '≤4',
        lte6: '≤6',
        gt6: '＞6',
      }
      if (!formMap.hoursOfAcOperation.value) return i18n.t('emptyValue')
      const value = keyValMap[formMap.hoursOfAcOperation.value]
      return i18n.t(`${titlePrefix}.hoursOfAcOperationOption`, [unref(value)])
    })
    const avgInDoorTempSpointForTenant = computed(() => {
      const keyValMap = {
        'gte23.5': '≥23.5℃',
        '23.0-23.4': '23.0-23.4℃',
        '22.5-22.9': '22.5-22.9℃',
        '22.0-22.4': '22.0-22.4℃',
        'lt22.0': '＜22.0℃',
        'na': i18n.t('na'),
      }
      if (!formMap.averageIndoorTemperatureSetpoint.value) return i18n.t('emptyValue')
      const value = keyValMap[formMap.averageIndoorTemperatureSetpoint.value]
      return value
    })
    const airConditionForTenant = computed(() => {
      return [
        { title: i18n.t(`${titlePrefix}.installationOfComfy`), value: unref(comfortForTenant), enabled: unref(buildingComfyInstallation) },
        { title: i18n.t(`${titlePrefix}.hoursOfAcOperation`), value: unref(hoursOfAcOpForTenant), enabled: true },
        { title: i18n.t(`${titlePrefix}.averageIndoorTemperatureSetpoint`), value: unref(avgInDoorTempSpointForTenant), enabled: true },
      ]
    })
    return {
      airConditionForTenant,
      isEdit,
      canViewPoint,
      isTenant,
      isHKlandUser,
      ...toRefs(formMap),
      points: computed(() => totalPoints(formMap)),
      since,
      sinceDate,
      buildingComfyInstallation,
    }
  },
}
</script>

<style lang="scss" scoped>
@import './common.scss';

.green-operation__assessment-form {
  .hkl-radio-button {
    padding-right: 10px;
  }
  ::v-deep input[type='radio'] + div.hkl-radio-button__slot {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    span.air-conditioning__form-since {
      flex-grow: 0;
      flex-shrink: 0;
      margin-right: 5px;
    }
    .vdp-datepicker {
      flex-grow: 1;
      margin-top: -10px;
    }
  }
}

</style>
